
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Subject, take, takeUntil } from 'rxjs';
import {
  Association,
  CrmAssociationService,
} from '../../../shared-services/crm-services/crm-object-service/crm-association.service';
import { PRIMARY_TAG } from '../../../constants';
import { TranslationModule } from '../../../i18n/translation-module';
import { EditAssociationTagsComponent } from '../edit-association-tags/edit-association-tags.component';
import { CompanyTableCustomizationService } from '../../../shared-services';

export interface SelectedRows {
  id: string;
  name: string;
}

export interface AssociationsDialogData {
  newSelection: string;
  modalTitle: string;
  primarySelect: string;
  description: string;
  selectedRows: SelectedRows[];
  associationType: string;
  associations: Association[];
}

@Component({
  selector: 'crm-select-associations-modal',
  templateUrl: './select-associations-modal.component.html',
  styleUrls: ['./select-associations-modal.component.scss'],
  imports: [
    TranslationModule,
    MatDialogModule,
    MatButtonModule,
    GalaxyFormFieldModule,
    MatRadioModule,
    ReactiveFormsModule,
    EditAssociationTagsComponent
],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: CompanyTableCustomizationService }],
})
export class SelectAssociationsModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AssociationsDialogData,
    private dialogRef: MatDialogRef<SelectAssociationsModalComponent>,
    private dialog: MatDialog,
    private readonly crmAssociationService: CrmAssociationService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.data.selectedRows.forEach((row, index) => {
      const association = this.fb.group({
        id: new FormControl(row.id || ''),
        tags: new FormControl<string[] | null>([]) as UntypedFormControl,
        name: new FormControl(row.name || ''),
        isPrimary: new FormControl(index === 0),
      });

      this.associationFormArray.push(association);

      association
        .get('tags')
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((tags: string[]) => {
          if (tags?.includes(PRIMARY_TAG)) {
            this.setPrimaryAssociation(index);
            const updatedTags = tags.filter((tag: string) => tag !== PRIMARY_TAG);
            association.get('tags')?.setValue(updatedTags, { emitEvent: false });
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  associationsFormGroup: FormGroup = this.fb.group({
    associations: this.fb.array([]),
  });

  get associationFormArray(): FormArray {
    return this.associationsFormGroup.get('associations') as FormArray;
  }

  private get associationTags(): Map<string, string[]> {
    const tagsMap = new Map<string, string[]>();
    this.associationFormArray.controls.forEach((control) => {
      const association = control as FormGroup;
      const tagsArray = association?.get('tags')?.value;
      if (tagsArray && tagsArray.length > 0) {
        tagsMap.set(association.get('id')?.value, tagsArray);
      }
    });
    return tagsMap;
  }

  private get primaryAssociation(): string {
    return this.associationFormArray.controls.find((control) => control.get('isPrimary')?.value)?.get('id')?.value;
  }

  cancel(): void {
    this.dialogRef?.close([]);
  }

  save(): void {
    this.crmAssociationService
      .createContactCompanyAssociation$(this.data.associationType, this.primaryAssociation, this.associationTags)
      .pipe(take(1))
      .subscribe((copyCrmObjects) => {
        return this.dialogRef.close(copyCrmObjects);
      });
  }

  setPrimaryAssociation(selectedIndex: number): void {
    this.associationFormArray.controls.forEach((control: AbstractControl, index: number) => {
      const association = control as FormGroup;
      association?.get('isPrimary')?.setValue(index === selectedIndex);
    });
  }

  tagsChanged(selectedIndex: number, tags: string[]): void {
    this.associationFormArray.controls[selectedIndex].get('tags')?.setValue(tags);
  }
}
